// InvoiceQueue — the contract manager's primary finance surface: a
// status-filterable, searchable review queue of the contract's invoices
// (search matches invoice number, charge labels, and notes, composing with
// the status chips; a persistent role="status" region announces the
// outcome). Loads every
// invoice once (unfiltered) so the chip counts and the filtered view derive
// client-side from a single fetch — a contract's invoice count is modest. The
// default filter is Awaiting review (the queue's reason for being). Each row
// summarises its approval-due date (invoiceDate + the overdue-age window,
// blank once approved — there's nothing left to be due), what the invoice
// pays for, its ex-/inc-GST total, an exceptions-only aggregate validation
// pill (rendered only when the derivation flags something; the near-limit
// warn rung is suppressed once approved — headroom then belongs to the
// Budget lines tab), an aging pill for
// unapproved invoices (Due soon at 20 days, Overdue at 30, measured against
// today's Brisbane date), and a status badge; the caret expands the row into
// InvoiceRowDetail (allocation breakdown + the approve/query actions).
//
// The Record-invoice button and the approve-success path are callback props —
// FinanceTab owns the modal seams those fill. Row-detail line labels resolve
// against a contract-wide line lookup built from every PO (the queue is
// contract-wide, so a row may belong to a PO other than the selected one),
// falling back to the selected PO for context. Each row also resolves its OWN
// PO (by `invoice.poId`) into the detail's `owningPo` prop, distinct from the
// `po` fallback (the currently-selected PO) — a later consumer needs the
// invoice's real PO, not whichever one happens to be selected.

import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { clsx } from "clsx";
import { ChevronRight, Plus } from "lucide-react";
import { LoadingSpinner, Alert } from "../../common";
import Button from "../../common/Button";
import ValidationPill from "./ValidationPill";
import InvoiceRowDetail from "./InvoiceRowDetail";
import { useInvoices } from "../../../hooks/useFinance";
import {
  formatAud,
  deriveInvoiceAging,
  invoiceApprovalDueDate,
  type InvoiceAging,
} from "../../../utils/financeCalc";
import { formatDayMonthYear } from "../../../utils/formatShortDate";
import { gstSuffix } from "../../../utils/gstSuffix";
import { matchesSowSearch } from "../tracking/shared/sowPickerSearch";
import type {
  InvoiceWithDerived,
  InvoiceStatus,
  PurchaseOrderWithDerived,
  ApproveInvoiceResponse,
  PoLine,
} from "../../../types/finance";

interface InvoiceQueueProps {
  contractId: string;
  /** The PO in context — the row-detail fallback when a row's own PO can't be
   *  resolved from `purchaseOrders`. */
  selectedPo: PurchaseOrderWithDerived;
  /** Every PO on the contract — the row detail resolves split line labels across
   *  all of them, since the queue is contract-wide. */
  purchaseOrders: PurchaseOrderWithDerived[];
  onRecordInvoice: () => void;
  onEditInvoice: (invoice: InvoiceWithDerived) => void;
  onApproved: (response: ApproveInvoiceResponse) => void;
  /** Reveal request for an invoice (budget-line click-through / deep link):
   *  once the target exists in the loaded list, the filter widens to All —
   *  the target may be any status — and the row expands. Applied once per
   *  request object; pass a NEW object to re-focus the same invoice. */
  focusRequest?: InvoiceFocusRequest | null;
  /** Fired after `focusRequest` has been applied so the owner can drop the
   *  reference — the applied-marker is local state, so without this a queue
   *  remount (sub-view flip and back) would re-apply a stale request over
   *  the operator's later filter choices. */
  onFocusApplied?: () => void;
}

export interface InvoiceFocusRequest {
  id: string;
}

type FilterValue = InvoiceStatus | "all";

const FILTERS: ReadonlyArray<{ value: FilterValue; label: string }> = [
  { value: "review", label: "Awaiting review" },
  { value: "draft", label: "Draft" },
  { value: "approved", label: "Approved" },
  { value: "queried", label: "Queried" },
  { value: "all", label: "All" },
];

const STATUS_BADGE: Record<InvoiceStatus, { className: string; label: string }> = {
  draft: { className: "badge-draft", label: "Draft" },
  review: { className: "badge-inReview", label: "In review" },
  approved: { className: "badge-completed", label: "Approved" },
  queried: { className: "badge-pending", label: "Queried" },
};

const CHIP_BASE =
  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-caption font-medium transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400";
const CHIP_ACTIVE =
  "border-brand-400 bg-brand-50 text-brand-700 dark:border-brand-500 dark:bg-brand-950 dark:text-brand-300";
const CHIP_INACTIVE =
  "border-slate-300 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700";

const COLUMN_COUNT = 8;

// First charge label plus a "+N more" when the invoice pays for several
// things. A WIP invoice may carry no charges yet, so the summary falls back
// to a dash rather than reading past the empty array.
function paysForSummary(invoice: InvoiceWithDerived): string {
  const charges = invoice.charges;
  const first = charges[0]?.label ?? "—";
  return charges.length > 1
    ? `${first} +${charges.length - 1} more`
    : first;
}

function StatusBadge({ status }: { status: InvoiceStatus }) {
  const { className, label } = STATUS_BADGE[status];
  return <span className={className}>{label}</span>;
}

// Amber at 20 days unapproved, red at 30 — the pill vocabulary here is
// payment-approval aging, distinct from milestone due-soon (violet).
const AGING_BADGE: Record<Exclude<InvoiceAging, "none">, { className: string; label: string }> = {
  "due-soon": { className: "badge-expiring", label: "Due soon" },
  overdue: { className: "badge-overdue", label: "Overdue" },
};

// Today's Brisbane date — QLD has no DST so the offset is stable year-round.
function brisbaneToday(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Australia/Brisbane" }).format(new Date());
}

export default function InvoiceQueue({
  contractId,
  selectedPo,
  purchaseOrders,
  onRecordInvoice,
  onEditInvoice,
  onApproved,
  focusRequest,
  onFocusApplied,
}: InvoiceQueueProps) {
  const invoicesQuery = useInvoices(contractId);
  const [filter, setFilter] = useState<FilterValue>("review");
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [appliedFocusRequest, setAppliedFocusRequest] =
    useState<InvoiceFocusRequest | null>(null);
  const today = brisbaneToday();

  // Render-time adjustment (not an effect): apply a pending focus request
  // once its target is actually in the loaded list (the list arrives async).
  // Guarded on request identity so it applies exactly once per request and
  // never fights manual filter/expand interaction afterwards.
  const loadedInvoices = invoicesQuery.data?.invoices;
  if (
    focusRequest &&
    focusRequest !== appliedFocusRequest &&
    loadedInvoices?.some((i) => i.id === focusRequest.id)
  ) {
    setAppliedFocusRequest(focusRequest);
    setFilter("all");
    setExpandedId(focusRequest.id);
  }

  // Report an applied request (effect, no local setState) so the owner drops
  // it — the applied-marker above dies with this component on unmount.
  useEffect(() => {
    if (focusRequest && focusRequest === appliedFocusRequest) {
      onFocusApplied?.();
    }
  }, [focusRequest, appliedFocusRequest, onFocusApplied]);

  // Scroll the revealed row into view once its focus applies — under the
  // All filter the target can sit well below the fold. Optional call: jsdom
  // has no scrollIntoView.
  const focusRowRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    if (appliedFocusRequest && focusRowRef.current) {
      focusRowRef.current.scrollIntoView?.({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [appliedFocusRequest]);

  // Contract-wide line lookup so a row belonging to a non-selected PO still
  // resolves its real line labels (line ids are unique per contract).
  const allLinesById = useMemo(() => {
    const map = new Map<string, PoLine>();
    for (const order of purchaseOrders) {
      for (const line of order.lines) map.set(line.id, line);
    }
    return map;
  }, [purchaseOrders]);

  if (invoicesQuery.isPending) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  if (invoicesQuery.error) {
    return (
      <Alert variant="error">Couldn't load invoices. Please try again.</Alert>
    );
  }

  const all = invoicesQuery.data.invoices;

  if (all.length === 0) {
    return (
      <div
        data-testid="finance-invoices-panel"
        className="rounded-lg border border-slate-200 bg-white px-4 py-12 text-center dark:border-slate-700 dark:bg-slate-800"
      >
        <h3 className="text-h4 text-slate-900 dark:text-slate-100">
          No invoices yet
        </h3>
        <p className="mt-1 text-body text-slate-500 dark:text-slate-400">
          Record a supplier invoice against this purchase order to start the
          review queue.
        </p>
        <div className="mt-4 flex justify-center">
          <Button
            icon={<Plus className="h-4 w-4" aria-hidden="true" />}
            onClick={onRecordInvoice}
          >
            Record invoice
          </Button>
        </div>
      </div>
    );
  }

  const counts: Record<FilterValue, number> = {
    review: all.filter((i) => i.status === "review").length,
    draft: all.filter((i) => i.status === "draft").length,
    approved: all.filter((i) => i.status === "approved").length,
    queried: all.filter((i) => i.status === "queried").length,
    all: all.length,
  };
  // Search composes with the status chips: chips narrow by status first,
  // then the query matches number / charge labels / notes. Chip counts stay
  // search-independent — they describe the queue, not the query.
  const statusRows =
    filter === "all" ? all : all.filter((i) => i.status === filter);
  const rows = statusRows.filter((i) =>
    matchesSowSearch(
      [i.invoiceNumber, ...i.charges.map((c) => c.label), i.notes ?? ""].join(
        " ",
      ),
      search,
    ),
  );
  // PO GST was removed at the header level (GST is decided per PoLine now),
  // and finance-tab figures are always ex-GST, so the Total column header
  // uses that fixed treatment rather than reading a per-PO flag.
  const suffix = gstSuffix(true);

  return (
    <div className="space-y-3" data-testid="finance-invoices-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div role="group" aria-label="Invoice status filter" className="flex flex-wrap gap-2">
          {FILTERS.map(({ value, label }) => {
            const active = filter === value;
            return (
              <button
                key={value}
                type="button"
                aria-pressed={active}
                onClick={() => setFilter(value)}
                className={clsx(CHIP_BASE, active ? CHIP_ACTIVE : CHIP_INACTIVE)}
              >
                <span>{label}</span>
                <span
                  className={clsx(
                    "rounded px-1 text-[10px] tabular-nums",
                    active
                      ? "bg-white text-brand-600 dark:bg-brand-900 dark:text-brand-300"
                      : "bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400",
                  )}
                >
                  {counts[value]}
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-2">
          <input
            type="search"
            className="input-sm w-56"
            placeholder="Search invoices…"
            aria-label="Search invoices"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button
            size="sm"
            icon={<Plus className="h-4 w-4" aria-hidden="true" />}
            onClick={onRecordInvoice}
          >
            Record invoice
          </Button>
        </div>
      </div>

      {/* Persistent polite live region (same convention as ChargeCatalogue):
          focus stays in the search input while the rows change, so this is
          the only channel assistive tech hears the outcome through. Visible
          only in the zero-result state; with matches the count is sr-only. */}
      <p
        role="status"
        className={clsx(
          search.trim() && rows.length === 0
            ? "text-body-sm text-slate-500 dark:text-slate-400"
            : "sr-only",
        )}
      >
        {search.trim()
          ? rows.length === 0
            ? `No invoices match “${search.trim()}”.`
            : rows.length === 1
              ? "1 invoice shown."
              : `${rows.length} invoices shown.`
          : ""}
      </p>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100">
        <div className="overflow-auto">
          <table className="min-w-full border-collapse text-body-sm">
            <thead className="bg-slate-50 dark:bg-slate-800">
              <tr className="text-left text-overline uppercase text-slate-500 dark:text-slate-400">
                <th scope="col" className="w-8 px-3 py-2">
                  <span className="sr-only">Expand</span>
                </th>
                <th scope="col" className="px-3 py-2 font-medium">Invoice #</th>
                <th scope="col" className="px-3 py-2 font-medium">Date</th>
                <th scope="col" className="px-3 py-2 font-medium">Approval due</th>
                <th scope="col" className="px-3 py-2 font-medium">What it pays for</th>
                <th scope="col" className="px-3 py-2 text-right font-medium">
                  Total <span className="normal-case text-slate-500">({suffix})</span>
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  <span className="sr-only">Exceptions</span>
                </th>
                <th scope="col" className="px-3 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="[&>tr:not(:first-child)]:border-t [&>tr:not(:first-child)]:border-t-slate-200 dark:[&>tr:not(:first-child)]:border-t-slate-700">
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={COLUMN_COUNT}
                    className="px-3 py-8 text-center text-body text-slate-500 dark:text-slate-400"
                  >
                    No invoices in this view.
                  </td>
                </tr>
              ) : (
                rows.map((invoice) => {
                  const expanded = expandedId === invoice.id;
                  // The queue is contract-wide: resolve the row's own PO so
                  // the detail's copy action can never stamp the selected
                  // PO's number over another PO's invoice.
                  const owningPo = purchaseOrders.find((p) => p.id === invoice.poId);
                  return (
                    <Fragment key={invoice.id}>
                      <tr
                        data-testid={`invoice-row-${invoice.id}`}
                        ref={
                          invoice.id === appliedFocusRequest?.id
                            ? focusRowRef
                            : null
                        }
                        className="align-top hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                      >
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            aria-label={`${expanded ? "Collapse" : "Expand"} invoice ${invoice.invoiceNumber}`}
                            aria-expanded={expanded}
                            onClick={() =>
                              setExpandedId(expanded ? null : invoice.id)
                            }
                            className={clsx(
                              "inline-flex rounded-sm transition-transform focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400",
                              expanded
                                ? "rotate-90 text-brand-600 dark:text-brand-400"
                                : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200",
                            )}
                          >
                            <ChevronRight className="h-4 w-4" aria-hidden="true" />
                          </button>
                        </td>
                        <td className="px-3 py-2 font-medium text-slate-900 dark:text-slate-100">
                          {invoice.invoiceNumber}
                        </td>
                        <td className="px-3 py-2 tabular-nums text-slate-600 dark:text-slate-400">
                          {formatDayMonthYear(invoice.invoiceDate)}
                        </td>
                        <td className="px-3 py-2 tabular-nums text-slate-600 dark:text-slate-400">
                          {invoice.status !== "approved" &&
                            (() => {
                              const due = invoiceApprovalDueDate(invoice);
                              return due ? formatDayMonthYear(due) : null;
                            })()}
                        </td>
                        <td className="px-3 py-2 text-slate-700 dark:text-slate-300">
                          {paysForSummary(invoice)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-900 dark:text-slate-100">
                          {formatAud(invoice.totalAmount)}
                        </td>
                        <td className="px-3 py-2">
                          {/* A warn aggregate can only be the balance near-limit
                              (price validation never yields warn). Once approved,
                              budget headroom is the Budget lines tab's concern,
                              so the pill goes quiet; over (overdrawn / price
                              exception) stays visible — that's a data problem. */}
                          {invoice.derived?.pill &&
                            !(
                              invoice.status === "approved" &&
                              invoice.derived.pill.kind === "warn"
                            ) && (
                              <ValidationPill
                                kind={invoice.derived.pill.kind}
                                label={invoice.derived.pill.label}
                              />
                            )}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap items-center gap-1">
                            {(() => {
                              const aging = deriveInvoiceAging(invoice, today);
                              if (aging === "none") return null;
                              const badge = AGING_BADGE[aging];
                              return (
                                <span className={badge.className}>
                                  {badge.label}
                                </span>
                              );
                            })()}
                            <StatusBadge status={invoice.status} />
                          </div>
                        </td>
                      </tr>
                      {expanded && (
                        <tr data-testid={`invoice-detail-${invoice.id}`}>
                          <td
                            colSpan={COLUMN_COUNT}
                            className="border-l-2 border-brand-500 bg-slate-50 p-3 dark:bg-slate-800/40"
                          >
                            <InvoiceRowDetail
                              contractId={contractId}
                              invoice={invoice}
                              po={selectedPo}
                              owningPo={owningPo}
                              allLinesById={allLinesById}
                              onApproved={onApproved}
                              onEdit={() => onEditInvoice(invoice)}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
